import {
  Card,
  CardContent,
  CardHeader,
  Collapse,
  IconButton,
  Stack,
  Switch,
  FormControlLabel,
  Typography,
  Checkbox,
} from "@mui/material";
import { useMutation, useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, notFound } from "@tanstack/react-router";
import Check from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import ListIcon from "@mui/icons-material/List";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  MutateAppPermissionsForm,
  appPermissionGroups,
} from "@dmca/frontend/components/display";
import { useEffect, useMemo, useState } from "react";
import { enum as _enum } from "valibot";
import { useAppPermissions } from "@dmca/frontend/hooks";
import { useForm } from "react-hook-form";
import {
  CognitoUserGroupPermissionDefaults,
  deserializeAppPermissions,
  getRoleDisplayName,
  ValidCognitoPermissionGroupsEnum,
  serializeAppPermissions,
  ValidCognitoPermissionGroupsOrder,
} from "@dmca/models/interface/auth";
import { Button } from "@dmca/frontend/components/button";
import { Input, Select } from "@dmca/frontend/components/input";
import { ConfirmationModal, Modal } from "@dmca/frontend/components/modal";
import { toast } from "react-toastify";

export const Route = createFileRoute("/_authed/_drawerLayout/admin/user/$id")({
  component: RouteComponent,
  loader: async ({ context: { trpcQcProxy: trpc, qc }, params: { id } }) => {
    const user = await qc.ensureQueryData(
      trpc.user.getOne.queryOptions({
        id,
      })
    );
    if (!user) {
      throw notFound();
    }
  },
});

function RouteComponent() {
  const { trpcQcProxy: trpc, qc, user: currentUser } = Route.useRouteContext();
  const navigate = Route.useNavigate();
  const hasPermission = useAppPermissions();
  const id = Route.useParams({
    select: (params) => params.id,
  });
  const { data: user } = useSuspenseQuery(
    trpc.user.getOne.queryOptions(
      {
        id,
      },
      {
        select: (user) => {
          if (!user) {
            throw new Error("User not found");
          }
          return user;
        },
      }
    )
  );
  const { data: kanbanDepartments = [] } = useQuery(
    trpc.kanban.getDepartments.queryOptions()
  );
  const [allowedDepartments, setAllowedDepartments] = useState<string[] | null>(
    user.kanbanDepartments ?? null
  );
  useEffect(
    () => setAllowedDepartments(user.kanbanDepartments ?? null),
    [user.kanbanDepartments]
  );
  const savedDepartments = user.kanbanDepartments ?? null;
  const departmentsChanged =
    allowedDepartments === null
      ? savedDepartments !== null
      : savedDepartments === null ||
        allowedDepartments.length !== savedDepartments.length ||
        allowedDepartments.some((id) => !savedDepartments.includes(id));

  const deleteMutation = useMutation(
    trpc.user.delete.mutationOptions({
      onSuccess: () => {
        toast.success("User deleted successfully.");
        qc.invalidateQueries(trpc.user.getAll.queryFilter());
        qc.invalidateQueries(trpc.workOrderClock.pathFilter());
        navigate({
          to: "/admin/user",
        });
      },
      onError: (e) => {
        console.error(e);
        toast.error(
          "An unknown error occurred. Please try again or contact support."
        );
      },
    })
  );

  const updateMutation = useMutation(
    trpc.user.update.mutationOptions({
      onSuccess: (_, variables) => {
        toast.success("User updated successfully.");
        qc.invalidateQueries(trpc.workOrderClock.pathFilter());
        qc.invalidateQueries(
          trpc.user.getOne.queryFilter({
            id,
          })
        );
        if (
          variables.kanbanDepartments !== undefined &&
          id === currentUser.id
        ) {
          qc.invalidateQueries({ queryKey: ["better-auth", "getSession"] });
          qc.invalidateQueries(trpc.kanban.getWithOrder.queryFilter());
          qc.invalidateQueries(trpc.kanban.getDepartments.queryFilter());
          qc.invalidateQueries(trpc.kanban.getImageUrls.queryFilter());
        }
      },
    })
  );

  const form = useForm({
    defaultValues: {
      permissions: deserializeAppPermissions(user.userPermissions),
    },
  });

  const {
    handleSubmit,
    reset,
    formState: { isDirty },
  } = form;

  const [userRole, setUserRole] = useState(user.role);
  const [showDeleteUserConfirmModal, setShowDeleteUserConfirmModal] =
    useState(false);
  const [showRoster, setShowRoster] = useState(false);
  const [pendingGeneralAccount, setPendingGeneralAccount] = useState<
    boolean | null
  >(null);

  const roleDefaultPermissions = useMemo(
    () =>
      deserializeAppPermissions(CognitoUserGroupPermissionDefaults[userRole]),
    [userRole]
  );

  useEffect(() => {
    reset({
      permissions: deserializeAppPermissions(user.userPermissions),
    });
    setUserRole(user.role);
  }, [id, reset, user.role, user.userPermissions]);

  const onPermissionsOverrideSubmit = handleSubmit((data) => {
    updateMutation.mutate(
      {
        id,
        userPermissions: serializeAppPermissions(data.permissions),
      },
      {
        onSuccess: () => {
          reset({
            permissions: data.permissions,
          });
        },
      }
    );
  });

  const onPermissionsOverrideReset = () => {
    reset();
  };

  return (
    <>
      <Input value={user.name} label="Name" disabled />
      <Card sx={{ mb: 1 }}>
        <CardHeader
          title="Kanban Departments"
          subheader="Choose which departments this user can see. All departments keeps existing access."
        />
        <CardContent>
          {currentUser.kanbanDepartments == null && (
            <FormControlLabel
              control={
                <Switch
                  checked={allowedDepartments === null}
                  onChange={(_, checked) =>
                    setAllowedDepartments(checked ? null : [])
                  }
                />
              }
              label="All departments"
            />
          )}
          {allowedDepartments !== null &&
            kanbanDepartments.map((department) => (
              <FormControlLabel
                key={department.id}
                control={
                  <Checkbox
                    checked={allowedDepartments.includes(department.id)}
                    onChange={(_, checked) =>
                      setAllowedDepartments((current) =>
                        checked
                          ? [...(current ?? []), department.id]
                          : (current ?? []).filter((id) => id !== department.id)
                      )
                    }
                  />
                }
                label={department.name}
              />
            ))}
          {departmentsChanged &&
            (currentUser.kanbanDepartments == null ||
              (allowedDepartments !== null &&
                allowedDepartments.every((department) =>
                  currentUser.kanbanDepartments?.includes(department)
                ))) && (
              <Button
                disabled={updateMutation.isPending}
                onClick={() =>
                  updateMutation.mutate({
                    id,
                    kanbanDepartments: allowedDepartments,
                  })
                }
              >
                Save departments
              </Button>
            )}
        </CardContent>
      </Card>
      <Card sx={{ mb: 1 }}>
        <CardHeader title="Set Role" />
        <CardContent>
          <Stack direction="row" spacing={1} alignItems="center">
            <Select
              options={ValidCognitoPermissionGroupsOrder.slice(
                ValidCognitoPermissionGroupsOrder.findIndex(
                  (r) => r === currentUser.role
                )
              ).map((x) => ({
                name: getRoleDisplayName(x),
                value: x,
              }))}
              value={userRole}
              onChange={(e) =>
                setUserRole(e.target.value as ValidCognitoPermissionGroupsEnum)
              }
              allowNull={false}
              required
              label="Role"
            />
            <Collapse
              in={userRole !== user.role}
              orientation="horizontal"
              sx={{ "&.MuiCollapse-hidden": { m: "0 !important" } }}
            >
              <Stack direction="row" spacing={1} justifyContent="flex-end">
                <IconButton
                  edge="end"
                  size="small"
                  disabled={updateMutation.isPending}
                  onClick={() => {
                    updateMutation.mutate({
                      id,
                      role: userRole,
                    });
                  }}
                >
                  <Check />
                </IconButton>
                <IconButton
                  edge="end"
                  size="small"
                  onClick={() => setUserRole(user.role)}
                >
                  <CloseIcon />
                </IconButton>
              </Stack>
            </Collapse>
          </Stack>
        </CardContent>
      </Card>
      <Card sx={{ mb: 1 }}>
        <CardHeader title="General Account" />
        <CardContent>
          <Stack direction="row" spacing={1} alignItems="center">
            <FormControlLabel
              control={
                <Switch
                  checked={user.isGeneralAccount}
                  disabled={updateMutation.isPending}
                  onChange={(_, checked) => setPendingGeneralAccount(checked)}
                />
              }
              label="General Account"
            />
            {user.isGeneralAccount && (
              <IconButton
                aria-label="Manage General Account workers"
                onClick={() => setShowRoster(true)}
              >
                <ListIcon />
              </IconButton>
            )}
          </Stack>
        </CardContent>
      </Card>
      {user.isGeneralAccount && (
        <GeneralAccountRoster
          accountId={id}
          open={showRoster}
          onClose={() => setShowRoster(false)}
        />
      )}
      <Card sx={{ mb: 1 }}>
        <CardHeader
          title="Permissions Override"
          subheader={`Unset permissions fall back to the ${getRoleDisplayName(
            userRole
          )} role's defaults, shown on each field.${
            userRole !== user.role
              ? " (Previewing an unsaved role change.)"
              : ""
          }`}
        />
        <CardContent
          component="form"
          onSubmit={onPermissionsOverrideSubmit}
          onReset={onPermissionsOverrideReset}
        >
          <MutateAppPermissionsForm
            form={form}
            defaultPermissions={roleDefaultPermissions}
            availablePermissionsFilterFunc={hasPermission}
            permissionGroups={appPermissionGroups}
          />
          <Collapse in={isDirty}>
            <Stack spacing={1} direction="row">
              <Button
                type="submit"
                fullWidth
                loading={updateMutation.isPending}
              >
                Submit
              </Button>
              <Button
                type="reset"
                fullWidth
                color="error"
                disabled={updateMutation.isPending}
              >
                Reset
              </Button>
            </Stack>
          </Collapse>
        </CardContent>
      </Card>
      <Card>
        <CardHeader title="Advanced" />
        <CardContent>
          <Button
            fullWidth
            color="error"
            onClick={() => setShowDeleteUserConfirmModal(true)}
            loading={deleteMutation.isPending}
          >
            Delete User
          </Button>
        </CardContent>
      </Card>
      <ConfirmationModal
        open={pendingGeneralAccount !== null}
        onClose={() => setPendingGeneralAccount(null)}
        message={
          pendingGeneralAccount
            ? "Convert to a General Account? This removes the personal PIN and ends open work sessions."
            : "Convert to a personal account? This deactivates roster workers, removes their PINs, and ends their open work sessions."
        }
        onConfirm={() => {
          if (pendingGeneralAccount !== null)
            updateMutation.mutate({
              id,
              isGeneralAccount: pendingGeneralAccount,
            });
        }}
      />
      <ConfirmationModal
        open={showDeleteUserConfirmModal}
        onClose={() => setShowDeleteUserConfirmModal(false)}
        onConfirm={() => {
          deleteMutation.mutate(
            {
              id,
            },
            {
              onSuccess: () => {
                setShowDeleteUserConfirmModal(false);
              },
            }
          );
        }}
      />
    </>
  );
}

function GeneralAccountRoster({
  accountId,
  open,
  onClose,
}: {
  accountId: string;
  open: boolean;
  onClose: () => void;
}) {
  const { trpcQcProxy: trpc, qc } = Route.useRouteContext();
  const { data: workers = [] } = useQuery({
    ...trpc.user.getGeneralWorkers.queryOptions({ accountId }),
    enabled: open,
  });
  const [editingId, setEditingId] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [pin, setPin] = useState("");
  const maskPinAsText =
    typeof CSS !== "undefined" && CSS.supports("-webkit-text-security", "disc");
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const refresh = () =>
    qc.invalidateQueries(trpc.user.getGeneralWorkers.queryFilter());
  const resetEntry = () => {
    setEditingId(null);
    setName("");
    setPin("");
  };
  const addWorker = useMutation(
    trpc.user.addGeneralWorker.mutationOptions({
      onSuccess: () => {
        void refresh();
        resetEntry();
        toast.success("Worker added.");
      },
      onError: (error) => toast.error(error.message),
    })
  );
  const updateWorker = useMutation(
    trpc.user.updateGeneralWorker.mutationOptions({
      onSuccess: () => {
        void refresh();
        resetEntry();
        toast.success("Worker updated.");
      },
      onError: (error) => toast.error(error.message),
    })
  );
  const deleteWorker = useMutation(
    trpc.user.deleteGeneralWorker.mutationOptions({
      onSuccess: () => {
        void refresh();
        qc.invalidateQueries(trpc.workOrderClock.pathFilter());
        setDeleteId(null);
        toast.success("Worker removed.");
      },
      onError: (error) => toast.error(error.message),
    })
  );
  const valid =
    !!name.trim() &&
    (!pin || /^[0-9]{5}$/.test(pin)) &&
    (!!editingId || /^[0-9]{5}$/.test(pin));

  return (
    <Modal
      open={open}
      onClose={onClose}
      header="General Account workers"
      fullWidth
      maxWidth="sm"
    >
      <Stack spacing={2}>
        {workers.map((worker) => (
          <Stack
            key={worker.id}
            direction="row"
            alignItems="center"
            spacing={1}
          >
            <Stack sx={{ flexGrow: 1, minWidth: 0 }}>
              <Typography>{worker.name}</Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ overflowWrap: "anywhere" }}
              >
                Worker ID: {worker.id}
              </Typography>
            </Stack>
            <IconButton
              aria-label={`Edit ${worker.name}, Worker ID ${worker.id}`}
              onClick={() => {
                setEditingId(worker.id);
                setName(worker.name);
                setPin("");
              }}
            >
              <EditIcon />
            </IconButton>
            <IconButton
              aria-label={`Remove ${worker.name}, Worker ID ${worker.id}`}
              onClick={() => setDeleteId(worker.id)}
            >
              <DeleteIcon />
            </IconButton>
          </Stack>
        ))}
        <Stack direction="row" spacing={1} alignItems="center">
          <IconButton aria-label="Add worker" onClick={resetEntry}>
            <AddIcon />
          </IconButton>
          <Input
            label="Worker name"
            autoComplete="off"
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
          <Input
            label={editingId ? "New PIN (optional)" : "Five-digit PIN"}
            type={maskPinAsText ? "text" : "password"}
            sx={
              maskPinAsText
                ? { "& input": { WebkitTextSecurity: "disc" } }
                : undefined
            }
            autoComplete="off"
            slotProps={{ htmlInput: { inputMode: "numeric", maxLength: 5 } }}
            value={pin}
            onChange={(event) =>
              setPin(event.target.value.replace(/\D/g, "").slice(0, 5))
            }
          />
        </Stack>
        <Button
          disabled={!valid}
          loading={addWorker.isPending || updateWorker.isPending}
          onClick={() =>
            editingId
              ? updateWorker.mutate({
                  workerId: editingId,
                  name,
                  pin: pin || undefined,
                })
              : addWorker.mutate({ accountId, name, pin })
          }
        >
          {editingId ? "Save worker" : "Add worker"}
        </Button>
      </Stack>
      <ConfirmationModal
        open={!!deleteId}
        onClose={() => setDeleteId(null)}
        onConfirm={() => {
          if (deleteId) deleteWorker.mutate({ workerId: deleteId });
        }}
      />
    </Modal>
  );
}
